import { Context, createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { OfflineIndicator } from '@ui';
import { getPermissionsList, PermissionType } from '@cp/core/permissions';
import { Routes, ROUTES } from '@cp/core/routing/constants';
import { usePlatform, useUpdatePlatformMode, PlatformType } from '@cp/core/platform';
import { amplifyAuthDefaultValues, PublicAmplifyAuthData, useAmplifyAuth } from '@cp/core/auth/useAmplifyAuth';
import {
  clearRedirectTargetOnArrival,
  getPostSignInPath,
  getRedirectTargetEntityId,
  rememberRedirectTarget,
} from '@cp/core/auth/redirectTarget';
import { Organization, OrganizationType, getOrganizationsFromProfile } from '@cp/core/organization';
import { CustomerRoleT } from '@/api/apis/customer';
import { ORGANIZATION_TENANT_PREFIX } from '@/constants/customer.constants';
import { userQueries } from '@/queries/user';
import { UserProfileType } from '@/api/apis/user';
import { wsDefaultConnector } from '@/ws';
import { ApiResponseStatus } from '@/constants/apiErrors.constants';
import { resetAllQueries } from '@/queries/utils';
import { queryClient } from '@/queries';
import { usePageFiltersMemory } from '@/hooks/usePageFiltersMemory';
import { clearRedirectTarget } from '@/utils/helpers/auth-redirect.helpers';

export type AuthProviderProps = Omit<PublicAmplifyAuthData, 'initMfaPreferences'> & {
  isUserDataInitialized: boolean;
  isOnline: boolean;
  selectedOrganization: Organization | null;
  roles: CustomerRoleT[];
  composerProfile: UserProfileType | null;
  getProfile: () => UserProfileType['customer'] | UserProfileType['opoContact'] | null;
  resetComposerProfile: () => void;
  refreshComposerProfile: () => Promise<void>;
  getUserSession: () => Promise<void>;
  checkPermission: (permission: PermissionType) => boolean;
  setSelectedOrganization: (org: Organization | null) => void;
  logout: (options?: { forceLogout?: boolean }) => Promise<void>;
  clientClearError: () => void;
};

export const AuthContext: Context<AuthProviderProps> = createContext<AuthProviderProps>({
  isUserDataInitialized: false,
  isOnline: true,
  selectedOrganization: null,
  roles: [],
  composerProfile: null,
  getProfile: () => null,
  resetComposerProfile: () => {},
  refreshComposerProfile: () => Promise.resolve(),
  getUserSession: () => Promise.resolve(),
  checkPermission: () => true,
  setSelectedOrganization: () => {},
  logout: () => Promise.resolve(),
  clientClearError: () => {},
  ...amplifyAuthDefaultValues,
});

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { entityId } = useParams<{ entityId: string }>();
  const updatePlatformMode = useUpdatePlatformMode();
  const { type: platformType } = usePlatform();
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [composerProfile, setComposerProfile] = useState<UserProfileType | null>(null);
  const [permissions, setPermissions] = useState<PermissionType[] | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [roles, setRoles] = useState<CustomerRoleT[] | []>([]);

  const publicUserRoutes = [ROUTES.SIGN_IN.ROOT, ROUTES.PASSWORD_RESET.ROOT];

  const isPublicUserRoute = publicUserRoutes.some((route) => location.pathname.includes(route));

  const {
    clientProfile,
    email,
    error,
    isOtpVerified,
    totpDetails,
    mfaState,
    resetClientProfile,
    setEmail,
    getClientProfileToken,
    setClientSession,
    login: clientLogin,
    forgotPassword,
    updatePassword: clientUpdatePassword,
    changePassword,
    resetPassword,
    resendSmsCode,
    confirmCode: clientConfirmCode,
    updatePhone: clientUpdatePhone,
    setupTOTP,
    verifyTOTP,
    getMfaPreferences,
    initMfaPreferences,
    selectMfaMethod,
    sendSmsVerificationCode,
    signOut,
    clearError: clientClearError,
  } = useAmplifyAuth<Routes>(ROUTES);

  useEffect(() => {
    clientClearError();
  }, [location.pathname, clientClearError]);

  useEffect(() => {
    clearRedirectTargetOnArrival(location.pathname);
  }, [location.pathname]);

  const clearAppData = useCallback(() => {
    setComposerProfile(null);
    resetClientProfile();
    setPermissions(null);
    setSelectedOrganization(null);
    updatePlatformMode(false);
    localStorage.removeItem('token');
    localStorage.removeItem('streamToken');
    localStorage.removeItem('streamChannelGrants');
    localStorage.removeItem('selectedOrganizationId');
    localStorage.removeItem('selectedOrganizationPrefix');
    usePageFiltersMemory.getState().clearAllPages();
    clearRedirectTarget();
    resetAllQueries();
  }, [resetClientProfile, updatePlatformMode]);

  useEffect(() => {
    if (!isLoggedIn && composerProfile) {
      clearAppData();
    }
  }, [clearAppData, composerProfile, isLoggedIn]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getUserSession = useCallback(async () => {
    const accessToken = await getClientProfileToken();

    setLoggedIn(!!accessToken);

    if (accessToken) {
      localStorage.setItem('token', accessToken);
      await setClientSession();
      await wsDefaultConnector.start();
    } else {
      // Read from window, not this render's route: a session check that started on the opened page can
      // finish after the redirect to sign-in.
      rememberRedirectTarget(globalThis.location.pathname, globalThis.location.search);
      if (!isPublicUserRoute) {
        navigate(ROUTES.SIGN_IN.ROOT);
      }
    }
  }, [getClientProfileToken, isPublicUserRoute, navigate, setClientSession]);

  const handleClientResponse = useCallback(
    async <T extends unknown[]>(clientCB: (...args: T) => Promise<void>, ...args: T) => {
      await clientCB(...args);
      await getUserSession();
    },
    [getUserSession],
  );

  const updateSelectedOrganization = useCallback(
    (org: Organization | null) => {
      const prefix =
        org?.type === OrganizationType.OPO ? ORGANIZATION_TENANT_PREFIX.OPO : ORGANIZATION_TENANT_PREFIX.TxCenter;
      localStorage.setItem('selectedOrganizationId', org?.id ?? '');
      localStorage.setItem('selectedOrganizationPrefix', org ? prefix : '');
      setSelectedOrganization(org);
      updatePlatformMode(org?.type === OrganizationType.OPO);
    },
    [setSelectedOrganization, updatePlatformMode],
  );

  useEffect(() => {
    if (!selectedOrganization || !composerProfile) {
      return;
    }
    const orgRoles = selectedOrganization.roles ?? [];
    setRoles(orgRoles);
    const permissionList = getPermissionsList(orgRoles);
    setPermissions(permissionList);
  }, [composerProfile, selectedOrganization]);

  useEffect(() => {
    if (!clientProfile && composerProfile) {
      resetAllQueries();
    }
  }, [composerProfile, clientProfile]);

  const checkPermission = useCallback(
    (currentPermission: PermissionType) =>
      Array.isArray(permissions) ? permissions.includes(currentPermission) : false,
    [permissions],
  );

  const getProfile = useCallback(() => {
    if (!composerProfile) return null;

    return platformType === PlatformType.OpoAccess ? composerProfile.opoContact : composerProfile.customer;
  }, [composerProfile, platformType]);

  const logout = useCallback(
    async ({ forceLogout = false } = {}) => {
      if (isLoggedIn || forceLogout) {
        await signOut();
        await getUserSession();
      }
      if (forceLogout) {
        clearAppData();
      }
    },
    [clearAppData, getUserSession, isLoggedIn, signOut],
  );

  const initComposerProfile = useCallback(async () => {
    try {
      const profileData = await queryClient.fetchQuery(userQueries.profile());
      await initMfaPreferences();
      setComposerProfile(profileData);

      const organizations = getOrganizationsFromProfile(profileData);

      // 0 memberships -> block navigation
      if (!organizations.length) {
        navigate(ROUTES.ORGANIZATION_REMOVED);
        return;
      }

      // TODO: BE doesn't return isAccepted* flags on opoContact in /profile yet - re-enable gate once fixed (next PR)
      const acceptanceSource = profileData.customer;

      if (
        acceptanceSource &&
        (!acceptanceSource.isAcceptedTermsOfServiceAndPrivacyPolicy ||
          !acceptanceSource.isAcceptedToReceiveNotifications)
      ) {
        navigate(ROUTES.ACCOUNT_ACTIVATION);
        return;
      }

      // 1 membership -> auto-select, skip selector
      if (organizations.length === 1) {
        updateSelectedOrganization(organizations[0]);
        if (isPublicUserRoute) {
          navigate(getPostSignInPath());
        }
        return;
      }

      // multi-membership -> the org of the opened link, else the saved selection, else show selector
      const linkedOrgId = entityId ?? getRedirectTargetEntityId();
      const savedOrgId = localStorage.getItem('selectedOrganizationId');
      const resolvedOrg =
        organizations.find((org) => org.id === linkedOrgId) ?? organizations.find((org) => org.id === savedOrgId);

      if (resolvedOrg) {
        updateSelectedOrganization(resolvedOrg);
        if (isPublicUserRoute) {
          navigate(getPostSignInPath());
        }
      } else {
        localStorage.removeItem('selectedOrganizationId');
        navigate(ROUTES.ORGANIZATION_SELECTION);
      }
    } catch (profileError) {
      // eslint-disable-next-line no-console
      console.error('❌ Profile initialization error after login:', profileError);
      if (
        isAxiosError(profileError) &&
        (profileError?.response?.status === ApiResponseStatus.BadRequest || !profileError?.response)
      ) {
        await logout();
        navigate(ROUTES.SIGN_IN.ROOT);
      }
    }
  }, [initMfaPreferences, entityId, navigate, updateSelectedOrganization, isPublicUserRoute, logout]);

  const refreshComposerProfile = useCallback(async () => {
    try {
      const profileData = await queryClient.fetchQuery(userQueries.profile());
      setComposerProfile(profileData);
    } catch (profileError) {
      // eslint-disable-next-line no-console
      console.error('❌ Profile refresh error:', profileError);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && clientProfile && !composerProfile) {
      initComposerProfile();
    }
  }, [initComposerProfile, isLoggedIn, clientProfile, composerProfile]);

  const contextValue = useMemo(
    () => ({
      // Authentication state
      isOnline,
      isUserDataInitialized: isLoggedIn && !!composerProfile && Array.isArray(permissions),

      // User profile and data
      composerProfile,
      roles,
      selectedOrganization,

      // Authentication actions
      login: (...args: Parameters<typeof clientLogin>) => handleClientResponse(clientLogin, ...args),
      logout,
      getUserSession,

      // Profile management
      refreshComposerProfile,
      resetComposerProfile: () => setComposerProfile(null),
      setSelectedOrganization: updateSelectedOrganization,

      // Permissions
      checkPermission,
      getProfile,

      // MFA state and actions
      isOtpVerified,
      mfaState,
      totpDetails,
      getMfaPreferences,
      selectMfaMethod,
      setupTOTP,
      verifyTOTP,

      // Email and phone
      email,
      setEmail,
      updatePhone: (...args: Parameters<typeof clientUpdatePhone>) => handleClientResponse(clientUpdatePhone, ...args),

      // Password management
      changePassword,
      forgotPassword,
      resetPassword,
      updatePassword: (...args: Parameters<typeof clientUpdatePassword>) =>
        handleClientResponse(clientUpdatePassword, ...args),

      // SMS and verification
      confirmCode: (...args: Parameters<typeof clientConfirmCode>) => handleClientResponse(clientConfirmCode, ...args),
      resendSmsCode,
      sendSmsVerificationCode,
      clientClearError,

      // Error handling
      error,
    }),
    [
      // Authentication state
      isLoggedIn, // used internally for isUserDataInitialized
      isOnline,
      permissions,

      // User profile and data
      composerProfile,
      roles,
      selectedOrganization,

      // Authentication actions
      clientLogin,
      logout,
      getUserSession,
      handleClientResponse,

      // Profile management
      refreshComposerProfile,
      updateSelectedOrganization,

      // Permissions
      checkPermission,
      getProfile,

      // MFA state and actions
      isOtpVerified,
      mfaState,
      totpDetails,
      getMfaPreferences,
      selectMfaMethod,
      setupTOTP,
      verifyTOTP,

      email,
      setEmail,
      clientUpdatePhone,

      changePassword,
      forgotPassword,
      resetPassword,
      clientUpdatePassword,

      clientConfirmCode,
      resendSmsCode,
      sendSmsVerificationCode,

      error,
      clientClearError,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <OfflineIndicator isOffline={!isOnline} />
    </AuthContext.Provider>
  );
};
